import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { useState } from "react";

function slug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function PhotoField({ field, value, onChange, tid }) {
  const [busy, setBusy] = useState(false);
  const has = value && (value.url || value.path);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/forms/me/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ type: "photo", url: res.data.url, path: res.data.path, name: res.data.name });
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className={`flex cursor-pointer items-center gap-4 rounded-xl border-2 border-dashed p-4 transition-colors ${has ? "border-green-300 bg-green-50/50" : "border-slate-300 bg-slate-50 hover:border-[#0F52BA]"}`} data-testid={`${tid}-photo`}>
      {has ? (
        <img src={`${API}${value.url}`} alt="uploaded" className="h-16 w-24 rounded-lg object-cover" />
      ) : (
        <span className="flex h-16 w-24 items-center justify-center rounded-lg bg-white text-slate-400">
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Camera className="h-6 w-6" />}
        </span>
      )}
      <span className="text-sm">
        <span className="flex items-center gap-1.5 font-semibold text-slate-700">
          {has && <CheckCircle2 className="h-4 w-4 text-green-600" />}
          {has ? "Photo added — tap to replace" : busy ? "Uploading…" : "Tap to take or upload a photo"}
        </span>
        <span className="text-xs text-slate-400">Use your phone camera or choose a file</span>
      </span>
      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} disabled={busy} data-testid={`${tid}-photo-input`} />
    </label>
  );
}

function FieldLabel({ field, invalid, cls = "mb-2" }) {
  return (
    <div className={`flex items-center gap-2 ${cls}`}>
      <Label className="text-sm font-semibold text-slate-700">
        {field.label}
        {field.required && <span className="ml-1 text-red-500" title="Required">*</span>}
      </Label>
      {invalid && <span className="text-xs font-semibold text-red-500" data-testid={`field-${field.key}-required-err`}>Required</span>}
    </div>
  );
}

export function FieldRenderer({ field, value, onChange, invalid = false }) {
  const tid = `field-${field.key}`;

  if (field.type === "checkboxes") {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      if (selected.includes(opt)) onChange(selected.filter((x) => x !== opt));
      else onChange([...selected, opt]);
    };
    return (
      <div data-testid={tid}>
        <FieldLabel field={field} invalid={invalid} cls="mb-3" />
        <div className="grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {field.options.map((opt) => {
            const checked = selected.includes(opt);
            return (
              <label
                key={opt}
                data-testid={`${tid}-opt-${slug(opt)}`}
                className={`flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 text-sm transition-colors ${
                  checked ? "border-[#0F52BA] bg-blue-50/70 text-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}
              >
                <Checkbox checked={checked} onCheckedChange={() => toggle(opt)} className="mt-0.5" />
                <span>{opt}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  if (field.type === "radio") {
    return (
      <div data-testid={tid}>
        <FieldLabel field={field} invalid={invalid} cls="mb-3" />
        <RadioGroup value={value || ""} onValueChange={onChange} className="flex flex-wrap gap-2.5">
          {field.options.map((opt) => {
            const checked = value === opt;
            return (
              <label
                key={opt}
                data-testid={`${tid}-opt-${slug(opt)}`}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3.5 py-2 text-sm transition-colors ${
                  checked ? "border-[#0F52BA] bg-blue-50/70 text-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}
              >
                <RadioGroupItem value={opt} />
                <span>{opt}</span>
              </label>
            );
          })}
        </RadioGroup>
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div data-testid={tid}>
        <FieldLabel field={field} invalid={invalid} />
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger className="h-12" data-testid={`${tid}-trigger`}>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={opt} value={opt} data-testid={`${tid}-opt-${slug(opt)}`}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div data-testid={tid}>
        <FieldLabel field={field} invalid={invalid} />
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          data-testid={`${tid}-input`}
          className="resize-y"
        />
      </div>
    );
  }

  if (field.type === "signature") {
    return (
      <div data-testid={tid}>
        <FieldLabel field={field} invalid={false} />
        <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-blue-50/50 p-4 text-sm text-slate-600" data-testid={`${tid}-esign-note`}>
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#0F52BA]" />
          <span>You'll sign this securely with a legally-binding e-signature when you tap <strong>Sign &amp; Submit</strong> — no drawing needed.</span>
        </div>
      </div>
    );
  }

  if (field.type === "photo") {
    return (
      <div data-testid={tid}>
        <FieldLabel field={field} invalid={invalid} />
        <PhotoField field={field} value={value} onChange={onChange} tid={tid} />
      </div>
    );
  }

  // text, date, time, number, email, tel
  return (
    <div data-testid={tid}>
      <FieldLabel field={field} invalid={invalid} />
      <Input
        type={field.type === "text" ? "text" : field.type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${tid}-input`}
        className={`h-12 ${invalid ? "border-red-400 focus-visible:ring-red-400" : ""}`}
      />
    </div>
  );
}
